import { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';
import { CoreApiError } from '@faro-lotv/service-wires';
import { validateNotNullishObject, validatePrimitive, validateOfExpectedValue } from '@faro-lotv/foundation';

const AuthContext = createContext(undefined);
/**
 * @returns the current AuthContext or error if not available
 */
function useAuthContext() {
    const context = useContext(AuthContext);
    if (!context) {
        throw Error("useAuthContext() must be used within an AuthContext");
    }
    return context;
}

/**
 * @param data the window message object to validate
 * @returns true if the data is a valid UlmRedirectMessage
 */
function isUlmRedirectMessage(data) {
    return (validateNotNullishObject(data, "UlmRedirectMessage") &&
        validatePrimitive(data, "identifier", "string") &&
        validateOfExpectedValue(data, "identifier", "UlmRedirectMessage") &&
        validatePrimitive(data, "redirectUrl", "string"));
}

/**
 * @returns The URL to the login page of the user module that redirects back to the current page
 * @param userModuleUrl Base URL of the user module
 */
/**
 * @returns The login URL to use for the login via the ULM
 * @param ulmUrl URL of the Unified Login Mask
 */
function getUlmLoginUrl(ulmUrl) {
    const loginUrl = new URL(`login?client=sphereViewer&parent_url=${window.encodeURIComponent(window.location.href)}`, ulmUrl);
    return loginUrl.toString();
}

const HttpCodes = {
    Unauthorized: 401,
    Forbidden: 403,
};
/**
 * The Auth Provider will manage the authentication with the Sphere Unified Login Mask (ULM)
 *
 * @returns A component providing the system authentication, selected depending on the runtime config
 */
function AuthProvider({ children, ulmUrl, onLogout, coreApiClient, onLoginFailed, onLogoutFailed, onShowLoadingScreen, }) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [shouldPromptLogin, setShouldPromptLogin] = useState(false);
    const [showLoadingScreen, setShowLoadingScreen] = useState(true);
    const iFrameUrl = useMemo(() => getUlmLoginUrl(ulmUrl), [ulmUrl]);
    useEffect(() => {
        async function checkLogin() {
            setShowLoadingScreen(true);
            try {
                await coreApiClient.getLoggedInUser();
                setIsLoggedIn(true);
            }
            catch (error) {
                if (!(error instanceof CoreApiError) ||
                    (error.status !== HttpCodes.Unauthorized &&
                        error.status !== HttpCodes.Forbidden)) {
                    onLoginFailed(error);
                }
                else {
                    setIsLoggedIn(false);
                }
            }
            setShowLoadingScreen(false);
        }
        checkLogin();
    }, [coreApiClient, onLoginFailed]);
    /**
     * Add event listener for window messages coming from the ULM.
     * Redirect to the received URL if the message is of type UlmRedirectMessage.
     */
    useEffect(() => {
        function onWindowMessage(message) {
            if (isUlmRedirectMessage(message.data)) {
                window.location.href = message.data.redirectUrl;
            }
        }
        window.addEventListener("message", onWindowMessage);
        return () => window.removeEventListener("message", onWindowMessage);
    }, []);
    // Inform parent to show loading screen
    useEffect(() => {
        onShowLoadingScreen(showLoadingScreen);
    }, [onShowLoadingScreen, showLoadingScreen]);
    const requestLogin = useCallback(() => {
        setShouldPromptLogin(true);
    }, []);
    /**
     * Logs out the user from the application
     * After a successful logout a new login is requested
     */
    const logout = useCallback(async () => {
        setShowLoadingScreen(true);
        try {
            await coreApiClient.logout();
            onLogout();
            requestLogin();
        }
        catch (error) {
            onLogoutFailed(error);
        }
        setShowLoadingScreen(false);
    }, [coreApiClient, onLogout, onLogoutFailed, requestLogin]);
    const value = useMemo(() => ({
        isLoggedIn,
        requestLogin,
        logout,
    }), [isLoggedIn, logout, requestLogin]);
    if (showLoadingScreen) {
        return null;
    }
    return (jsx(AuthContext.Provider, { value: value, children: shouldPromptLogin && !isLoggedIn ? (jsx("iframe", { title: "unified-login-mask", style: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                border: "none",
            }, src: iFrameUrl })) : (children) }));
}

export { AuthContext, AuthProvider, isUlmRedirectMessage, useAuthContext };
